import React, { useEffect, useState } from 'react'
import styled from 'styled-components'

import { Grid as MuiGrid, IconButton, TextField, Typography } from '@material-ui/core'
import { spacing } from '@material-ui/system'
import { makeStyles } from '@material-ui/core/styles'
import { Clear as ClearIcon, Done as DoneIcon, Edit as EditIcon } from '@material-ui/icons'

import { Address } from '../../api/models'

const Grid = styled(MuiGrid)(spacing)

const useStyles = makeStyles(() => ({
  iconButton: {
    padding: '0',
  },
  edit: {
    fontSize: '12px',
    cursor: 'pointer',
  },
  save: {
    fontSize: '16px',
    cursor: 'pointer',
    color: 'green',
  },
  cancel: {
    fontSize: '16px',
    cursor: 'pointer',
    color: 'red',
  },
  hidden: {
    display: 'none',
  },
}))

interface ButtonControlProps {
  setEditMode: (active: boolean) => void
}

const EditControls = (props: ButtonControlProps) => {
  const { setEditMode } = props
  const classes = useStyles()
  return (
    <Grid item>
      <IconButton
        className={classes.iconButton}
        type="button"
        onClick={(e) => {
          e.preventDefault()
          setEditMode(true)
        }}
      >
        <EditIcon className={classes.edit} />
      </IconButton>
    </Grid>
  )
}

const SaveControls = (props: ButtonControlProps) => {
  const { setEditMode } = props
  const classes = useStyles()
  return (
    <>
      <Grid item>
        <IconButton className={classes.iconButton} type="submit">
          <DoneIcon className={classes.save} />
        </IconButton>
      </Grid>
      <Grid item>
        <IconButton className={classes.iconButton} type="button" onClick={() => setEditMode(false)}>
          <ClearIcon className={classes.cancel} />
        </IconButton>
      </Grid>
    </>
  )
}

interface FieldProps {}

interface CharFieldProps extends FieldProps {
  name: string
  // TODO: be more precise and remove the any hackery
  form?: any
  value?: string
}

export const EditableCharField = (props: CharFieldProps) => {
  const { name, value, form } = props
  const [editMode, setEditMode] = useState(false)
  const [hover, setHover] = useState(false)

  useEffect(() => {
    if (form) {
      form.success.register(() => setEditMode(false))
    }
    return () => {
      if (form) {
        form.success.deregister(() => setEditMode(false))
      }
    }
  }, [form])

  return (
    <Grid
      container
      spacing={1}
      alignItems="center"
      justify="flex-end"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {editMode ? (
        <>
          <SaveControls setEditMode={setEditMode} />
          <Grid item>
            <TextField
              defaultValue={value}
              id={name}
              name={name}
              size="small"
              inputRef={form?.form?.register()}
              error={name in form?.form?.errors}
              helperText={form?.form?.errors.name && form?.errors.name.message}
            />
          </Grid>
        </>
      ) : (
        <>
          {form?.form && hover && <EditControls setEditMode={setEditMode} />}
          <Grid item>
            <span>{value === undefined ? '-' : value}</span>
          </Grid>
        </>
      )}
    </Grid>
  )
}

interface AddressFieldProps extends FieldProps {
  name: string
  // TODO: be more precise and remove the any hackery
  form?: any
  value?: Address
}

export const EditableAddressField = (props: AddressFieldProps) => {
  const { name, value, form } = props
  const [editMode, setEditMode] = useState(false)
  const [hover, setHover] = useState(false)

  useEffect(() => {
    if (form) {
      form.success.register(() => setEditMode(false))
    }
    return () => {
      if (form) {
        form.success.deregister(() => setEditMode(false))
      }
    }
  }, [form])

  return (
    <Grid
      container
      spacing={1}
      alignItems="center"
      justify="flex-end"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {editMode ? (
        <>
          <SaveControls setEditMode={setEditMode} />
          <Grid container spacing={1}>
            <Grid item md={6}>
              <TextField
                defaultValue={value?.address1}
                id={`${name}.address1`}
                name={`${name}.address1`}
                label="Address 1"
                size="small"
                inputRef={form?.form?.register()}
                error={name in form?.form?.errors}
                helperText={form?.form?.errors.name && form?.errors.name.message}
                fullWidth
              />
            </Grid>
            <Grid item md={6}>
              <TextField
                defaultValue={value?.address2}
                id={`${name}.address2`}
                name={`${name}.address2`}
                label="Address 2"
                size="small"
                inputRef={form?.form?.register()}
                error={name in form?.form?.errors}
                helperText={form?.form?.errors.name && form?.errors.name.message}
                fullWidth
              />
            </Grid>
            <Grid item md={4}>
              <TextField
                defaultValue={value?.city}
                id={`${name}.city`}
                name={`${name}.city`}
                label="City"
                size="small"
                inputRef={form?.form?.register()}
                error={name in form?.form?.errors}
                helperText={form?.form?.errors.name && form?.errors.name.message}
                fullWidth
              />
            </Grid>
            <Grid item md={4}>
              <TextField
                defaultValue={value?.state}
                id={`${name}.state`}
                name={`${name}.state`}
                label="State"
                size="small"
                inputRef={form?.form?.register()}
                error={name in form?.form?.errors}
                helperText={form?.form?.errors.name && form?.errors.name.message}
                fullWidth
              />
            </Grid>
            <Grid item md={4}>
              <TextField
                defaultValue={value?.postalCode}
                id={`${name}.postal_code`}
                name={`${name}.postal_code`}
                label="Postal Code"
                size="small"
                inputRef={form?.form?.register()}
                error={name in form?.form?.errors}
                helperText={form?.form?.errors.name && form?.errors.name.message}
                fullWidth
              />
            </Grid>
          </Grid>
        </>
      ) : (
        <>
          {value === undefined ? (
            <span>-</span>
          ) : (
            <>
              {form?.form && hover && <EditControls setEditMode={setEditMode} />}
              <Grid item>
                <Typography>{value.address1}</Typography>
                {value.address2 && <Typography>{value.address2}</Typography>}
                <Typography>
                  {value.city}, {value.state} {value.postalCode}
                </Typography>
              </Grid>
            </>
          )}
        </>
      )}
    </Grid>
  )
}
